import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineCheckCircle, HiOutlineXMark } from "react-icons/hi2";

import { useExportList } from "@/hooks/api/list/useExportList";
import { useToast } from "@/hooks/useToast";
import Button from "@/components/atoms/Button";
import Spinner from "@/components/atoms/Spinner";

type Props = {
  listId: string;
  listName: string;
  visibleColumnIds: string[];
};

const ExportListButton = ({ listId, listName, visibleColumnIds }: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const { exportList } = useExportList();

  const handleExport = useCallback(async () => {
    setIsExporting(true);

    try {
      await exportList({ id: listId, listName, visibleColumnIds, t });

      showToast({
        featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
        heading: t("list.export.success"),
      });
    } catch {
      showToast({
        featuredIcon: { icon: HiOutlineXMark, color: "error" },
        heading: t("list.export.error"),
      });
    } finally {
      setIsExporting(false);
    }
  }, [exportList, listName, t]);

  return (
    <div>
      <Button
        label={
          isExporting ? (
            <>
              <Spinner size={"2xs"} /> {t("list.export.exporting")}
            </>
          ) : (
            t("list.export.export")
          )
        }
        onClick={handleExport}
        disabled={isExporting}
      />
    </div>
  );
};

export default ExportListButton;
